import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { mockZones } from '../../data/mockData';

export default function LayoutView() {
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6 pb-20 md:pb-6"
    >
      <div className="flex justify-between items-center bg-garden-dark p-4 rounded-xl border border-garden-base">
        <div>
          <h2 className="text-2xl font-bold text-clay">Zone Mapping</h2>
          <p className="text-sm text-clay-muted">Spatial allocation and microclimate visualization.</p>
        </div>
        <button className="w-10 h-10 rounded-lg bg-garden-base flex items-center justify-center text-clay hover:text-lime transition-colors">
          <SafeIcon icon="Maximize" />
        </button>
      </div>

      {/* Abstract Map Area */}
      <div className="relative w-full h-[400px] border border-garden-base rounded-xl bg-garden-darkest overflow-hidden bg-grid-pattern">
        {/* Decorative elements to look like a blueprint/cockpit map */}
        <div className="absolute top-4 left-4 flex gap-2">
           <div className="w-2 h-2 bg-lime rounded-full"></div>
           <div className="w-2 h-2 bg-red-400 rounded-full"></div>
           <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
        </div>
        <div className="absolute bottom-4 right-4 text-[10px] font-mono text-clay-dark text-right">
          <p>GRID SCALE: 1:10</p>
          <p>ORIENTATION: SOUTH-EAST</p>
        </div>

        {/* Mock Zones placed abstractly */}
        <div className="absolute inset-0 p-8">
          <div className="relative w-full h-full">
            {/* Zone 1: Balcony */}
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="absolute top-[10%] left-[10%] w-[40%] h-[30%] border-2 border-lime/30 bg-lime/5 rounded-lg p-3 cursor-pointer hover:border-lime/60 transition-colors glass-panel"
            >
              <div className="flex justify-between items-start">
                <span className="text-xs font-mono text-lime">Z-01 {mockZones[0].name}</span>
                <SafeIcon icon="Sun" className="text-yellow-400" />
              </div>
              <div className="mt-4 grid grid-cols-5 gap-1">
                {[...Array(mockZones[0].capacity)].map((_, i) => (
                  <div key={i} className={`h-8 rounded-sm ${i < mockZones[0].current ? 'bg-lime/80' : 'bg-garden-base/50'}`}></div>
                ))}
              </div>
            </motion.div>

            {/* Zone 2: Living Room */}
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="absolute bottom-[20%] right-[10%] w-[30%] h-[40%] border-2 border-clay-dark/30 bg-garden-base/10 rounded-lg p-3 cursor-pointer hover:border-clay-muted/60 transition-colors glass-panel"
            >
              <div className="flex justify-between items-start">
                <span className="text-xs font-mono text-clay-muted">Z-02 {mockZones[1].name}</span>
                <SafeIcon icon="Cloud" className="text-clay-dark" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {[...Array(mockZones[1].capacity)].map((_, i) => (
                  <div key={i} className={`h-12 rounded-sm ${i < mockZones[1].current ? 'bg-clay-muted/80' : 'bg-garden-base/50'}`}></div>
                ))}
              </div>
            </motion.div>

            {/* Zone 3: Kitchen */}
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="absolute top-[50%] left-[20%] w-[25%] h-[20%] border-2 border-blue-400/30 bg-blue-400/5 rounded-lg p-3 cursor-pointer hover:border-blue-400/60 transition-colors glass-panel"
            >
              <div className="flex justify-between items-start">
                <span className="text-xs font-mono text-blue-400">Z-03 {mockZones[2].name}</span>
                <SafeIcon icon="Droplet" className="text-blue-400" />
              </div>
               <div className="mt-2 flex gap-1">
                {[...Array(mockZones[2].capacity)].map((_, i) => (
                  <div key={i} className={`w-6 h-6 rounded-full ${i < mockZones[2].current ? 'bg-blue-400/80' : 'bg-garden-base/50 border border-blue-400/20'}`}></div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {mockZones.map(zone => (
          <div key={zone.id} className="bg-garden-dark p-4 rounded-lg border border-garden-base">
            <h4 className="text-sm font-medium text-clay mb-2">{zone.name}</h4>
            <div className="flex justify-between text-xs text-clay-muted mb-1">
              <span>Utilization</span>
              <span>{Math.round((zone.current/zone.capacity)*100)}%</span>
            </div>
            <div className="w-full h-1.5 bg-garden-base rounded-full overflow-hidden">
              <div className="h-full bg-lime" style={{ width: `${(zone.current/zone.capacity)*100}%` }}></div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}